<!-- @format -->

<template>
	<PublicPageLayout heading="§public.register_heading">
		<CoreStringInput
			v-model="inputs.name"
			:validator="v$.name"
			:fullWidth="true"
			placeholder="§ui.placeholder_input_name"
			class="mb-10"
		/>

		<CoreStringInput
			v-model="inputs.email"
			:validator="v$.email"
			:fullWidth="true"
			:disabled="true"
			placeholder="§ui.placeholder_login_email"
			class="mb-10"
		/>

		<CorePasswordInput
			v-model="inputs.password"
			:validator="v$.password"
			:fullWidth="true"
			class="mb-10"
		/>

		<CorePasswordInput
			v-model="inputs.passwordConfirm"
			:validator="v$.passwordConfirm"
			:fullWidth="true"
			class="mb-10"
			placeholder="§ui.placeholder_confirm_password"
		/>

		<CoreDropdown
			v-model="inputs.preferred_locale"
			:options="getLocaleOptions"
			:validator="v$.preferred_locale"
			:fullWidth="true"
			placeholder="§ui.placeholder_select_language"
			class="mb-10"
		/>

		<CoreButton
			@click="handleSubmit"
			label="§ui_button.save"
			icon="pi-save"
			:loading="waiting"
			:disabled="!isValid"
		/>
	</PublicPageLayout>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, email as emailRule } from '@vuelidate/validators'
import PublicPageLayout from './components/PublicPageLayout.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CorePasswordInput from '@/components/core/CorePasswordInput.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import { localeOptions } from '@/helpers/localeOptions'
import { useAuthStore } from '@/stores/auth'

const noSpaces = (value) => !/\s/.test(value)
const isSame = (inputs) => (value) => inputs.password === value

export default {
	name: 'InternalRegister',

	components: {
		PublicPageLayout,
		CoreStringInput,
		CorePasswordInput,
		CoreDropdown,
		CoreButton,
	},

	data() {
		return {
			inputs: {
				name: '',
				email: '',
				password: '',
				passwordConfirm: '',
				preferred_locale: '',
			},
			invite: '',
			waiting: false,
			v$: {},
		}
	},

	computed: {
		isValid() {
			return this.v$ ? !this.v$.$invalid : false
		},
		getLocaleOptions() {
			return localeOptions({ languagesOnly: true })
		},

		rules() {
			return {
				name: { required, minLength: minLength(2), maxLength: maxLength(100) },
				email: { required, minLength: minLength(2), maxLength: maxLength(100), emailRule },
				password: {
					required,
					minLength: minLength(6),
					maxLength: maxLength(100),
					noSpaces,
				},
				passwordConfirm: {
					required,
					minLength: minLength(6),
					maxLength: maxLength(100),
					sameAsPassword: isSame(this.inputs),
				},
				preferred_locale: { required },
			}
		},
	},

	watch: {
		'inputs.preferred_locale'(newValue) {
			this.$i18n.locale = newValue
		},
	},

	created() {
		this.authStore = useAuthStore()
	},

	mounted() {
		try {
			const data = JSON.parse(localStorage.getItem('internalInviteData') || '{}')
			this.v$ = useVuelidate(this.rules, this.inputs)
			this.inputs.email = data.user_email || ''
			this.setInvite()
		} catch (error) {
			console.error('Failed to parse internal invite data from localStorage:', error)
			this.v$ = useVuelidate(this.rules, this.inputs)
			this.setInvite()
		}
	},

	unmounted() {
		localStorage.removeItem('internalInviteData')
	},

	methods: {
		async handleSubmit() {
			this.v$.$touch()
			if (this.waiting || this.v$.$invalid) return

			this.waiting = true
			try {
				await this.authStore.internalRegister({
					invite: this.invite,
					name: this.inputs.name,
					password: this.inputs.password,
					preferred_locale: this.inputs.preferred_locale,
				})
				this.waiting = false
			} catch (error) {
				this.$toast.add({
					severity: 'error',
					summary: 'Error',
					detail: error.response?.data?.message || error.message || 'Registration failed',
					life: 2000,
				})
				this.waiting = false
			}
		},

		setInvite() {
			const url = new URL(window.location.href)
			const parts = url.pathname.split('/')
			this.invite = parts[parts.length - 1]
		},
	},
}
</script>
